import { handleSuccessfulSubmission } from "common";
import { produce } from "immer";
import { isString } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Merchandise, Payment, PaymentMethod, Purchase, PurchaseStatus } from "types";
import { object } from "yup";
import { api } from "@/api/api";
import { queryClient } from "@/api/queryClient";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { PaymentInputRef, TokenizePaymentParams } from "@/components/Input/PaymentInputRef";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { CheckoutFormValues } from "@/components/PurchaseCreate/CheckoutPurchaseScreen.types";
import { CheckoutPurchaseScreenFooter } from "@/components/PurchaseCreate/CheckoutPurchaseScreenFooter";
import { CreatePurchaseCustomerFields } from "@/components/PurchaseCreate/CreatePurchaseCustomerFields";
import { CreatePurchaseInternalNoteFields } from "@/components/PurchaseCreate/CreatePurchaseInternalNoteFields";
import { CreatePurchaseMerchandiseFields } from "@/components/PurchaseCreate/CreatePurchaseMerchandiseFields";
import { CreatePurchasePaymentDetails } from "@/components/PurchaseCreate/CreatePurchasePaymentDetails";
import { CreatePurchaseSidebar } from "@/components/PurchaseCreate/CreatePurchaseSidebar";
import { CustomLineItemList } from "@/components/PurchaseCreate/CustomLineItemList";
import {
    getPayload,
    getPostCreateBookingAction,
    hasCouponApplied,
    hasZeroDiscountCoupon,
    isPurchaseNotFoundError,
    refetchMerchandiseCatalogItems,
    resetPurchase,
} from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { useCheckoutPurchaseInitialValues } from "@/components/PurchaseCreate/hooks/useCheckoutPurchaseInitialValues";
import { useMerchandiseCheckoutValidations } from "@/components/PurchaseCreate/hooks/useCheckoutPurchaseScreenValidations";
import { useSetCustomerNameRequired } from "@/components/PurchaseCreate/hooks/useSetCustomerNameRequired";
import { IncompleteAutomaticFulfillmentModal } from "@/components/PurchaseCreate/Merchandise/IncompleteAutomaticFulfillmentModal";
import { RemoveCouponConfirmModal } from "@/components/PurchaseCreate/RemoveCouponConfirmModal";
import { useCustomerDetailsStore } from "@/components/PurchaseCreate/store/customerDetailsStore";
import { usePurchaseResponseStore } from "@/components/PurchaseCreate/store/usePurchaseResponseStore";
import { BillingInfoError } from "@/errors/requireBillingInfoError";
import { getAuthNetPurchasePayload, handlePaymentError } from "@/helpers/authNetPaymentHelper";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getCashPaymentLineItem, hasCreditCardLineItem, isReservePurchaseProcess } from "@/models/CreatePurchaseState";
import { hasIncompleteAutoFulfillment } from "@/models/MerchandisePurchaseItem";
import { getCreditCardLineItem, isHold, needsPrepare } from "@/models/Purchase";
import { invalidateSellerBalance, isAuthorizeNetEnabled } from "@/models/Seller";
import { clearDraftPurchasesCache, CreatePurchaseState, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface CheckoutMerchandisePurchaseScreenProps {
    merchandise: Merchandise;
    onBack: () => void;
}

interface IncompleteAutoFulfillmentState {
    purchase: Purchase;
    uri: string;
}

export const CheckoutMerchandisePurchaseScreen = ({ merchandise, onBack }: CheckoutMerchandisePurchaseScreenProps) => {
    const seller = getSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const paymentInputRef = useRef<PaymentInputRef>(null);

    const purchaseBalance = useCreatePurchaseStore((state) => state.balance);
    const { resetState, setIsEMV } = useCreatePurchaseStore.getState().actions;
    const totalQuantity = useCreatePurchaseStore((state) => state.getActiveItem()?.quantity ?? 0);
    const validationSchema = useMerchandiseCheckoutValidations();

    const [isSuccess, setIsSuccess] = useState(false);
    const [incompleteAutoFulfillmentState, setIncompleteAutoFulfillmentState] =
        useState<IncompleteAutoFulfillmentState | null>(null);
    const removeCouponModal = useModal();

    useSetCustomerNameRequired(merchandise);

    const shouldShowPaymentMethods = purchaseBalance !== 0;
    const showCustomerFieldsPreference = useCustomerDetailsStore((state) => state.showCustomerFields);

    const { mutateAsync: upsertPurchase, isLoading } = useMutation(async (payload: CreatePurchaseState) => {
        let data;
        if (payload.id) {
            ({ data } = await api.put<Purchase>(`/purchases/${payload.id}`, payload));
        } else {
            ({ data } = await api.post<Purchase>(`/purchases?seller=${seller.id}`, payload));
        }

        return data;
    });

    const refreshMerchandiseData = () => {
        void refetchMerchandiseCatalogItems(merchandise.id);
        invalidate("products", merchandise.id);
    };

    const initialValues = useCheckoutPurchaseInitialValues();

    const getPayloadForPaymentGateway = async (payload: CreatePurchaseState) => {
        const { cardInputMethod, paymentLineItem } = useCreatePurchaseStore.getState();
        const paymentStatus = paymentLineItem.status;

        if (isAuthorizeNetEnabled(seller) && hasCreditCardLineItem(payload)) {
            if (isString(paymentLineItem.payment.card)) {
                return payload;
            }

            const response = (await paymentInputRef.current?.tokenize()) as TokenizePaymentParams;
            if (!response) {
                return;
            }

            return getAuthNetPurchasePayload(response, payload, paymentStatus, cardInputMethod);
        }

        return payload;
    };

    const handleSubmit = async (values: CheckoutFormValues) => {
        if (totalQuantity === 0) return;

        const { paymentLineItem } = useCreatePurchaseStore.getState();

        const payload = getPayload(values) as CreatePurchaseState;

        if (hasZeroDiscountCoupon(payload.items)) {
            removeCouponModal.show();
            return;
        }

        let purchasePayload = await getPayloadForPaymentGateway(payload);
        if (!purchasePayload) {
            return;
        }

        const { dueNow = 0 } = usePurchaseResponseStore.getState().purchase;

        const paymentInputParams = { amountDue: dueNow };
        const paymentMethod = paymentLineItem.payment.method;
        const isCashPayment = paymentMethod === PaymentMethod.CASH;
        const isPayLater = paymentMethod === PaymentMethod.LATER;
        const isReservePurchase = isReservePurchaseProcess(paymentMethod, dueNow, paymentLineItem.status);
        const shouldPrepare = isCashPayment || (!isPayLater && paymentInputRef.current && needsPrepare());

        if (shouldPrepare && !isReservePurchase) {
            const response = await paymentInputRef.current?.prepare?.(paymentInputParams);
            if (!response) {
                return;
            }

            if (isCashPayment) {
                purchasePayload = produce(purchasePayload, (draft) => {
                    const paymentLineItem = getCashPaymentLineItem(draft.lineItems);
                    if (paymentLineItem) {
                        paymentLineItem.payment = response as Payment;
                    }
                });
            }
        }

        useCreatePurchaseStore.setState({ isSubmitting: true });
        let hasCreatedPurchase = false;

        try {
            const purchaseResponse = await upsertPurchase(purchasePayload);
            hasCreatedPurchase = true;

            const creditCardPaymentLineItem = getCreditCardLineItem(purchaseResponse);
            const clientSecret = creditCardPaymentLineItem?.payment?.paymentIntent?.clientSecret;
            if (clientSecret) {
                try {
                    await paymentInputRef.current?.confirm({ clientSecret });
                } catch (error) {
                    if (hasCreatedPurchase) {
                        refreshMerchandiseData();
                    }

                    if (isHold(purchaseResponse)) {
                        // Release the order if the payment failed otherwise the availability will be held up
                        if (!(error instanceof BillingInfoError)) {
                            await api.delete(`/purchases/${purchaseResponse.id}`);
                        }
                    } else {
                        throw error;
                    }

                    return;
                }
            }

            let finalPurchase = purchaseResponse;

            if (isHold(purchaseResponse) && !isAuthorizeNetEnabled(seller)) {
                const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${purchaseResponse.id}`, {
                    status: PurchaseStatus.COMMITTED,
                    lineItems: purchaseResponse.lineItems.map((lineItem) => ({
                        id: lineItem.id,
                        status: paymentLineItem.status,
                    })),
                });
                const { isEMV } = useCreatePurchaseStore.getState();
                if (isEMV) {
                    setIsEMV(false);
                }

                finalPurchase = updatedPurchase;
            }

            handlePaymentSuccess(finalPurchase);

            if (hasCouponApplied(payload.items)) {
                invalidate("codes");
            }

            if (finalPurchase.status === PurchaseStatus.COMMITTED) {
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    draft.status = PurchaseStatus.COMMITTED;
                });
                usePurchaseSearchStore.getState().setSearchQuery("");
                clearDraftPurchasesCache();
            }
        } catch (error: any) {
            if (isPurchaseNotFoundError(error)) {
                resetPurchase();
            }

            if (hasCreatedPurchase) {
                refreshMerchandiseData();
            }

            handlePaymentError(error, seller, paymentInputRef);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const handlePaymentSuccess = (updatedPurchase: Purchase) => {
        refreshMerchandiseData();
        updateCache("purchaseItems", updatedPurchase.items);
        // Don't refetch inactive queries: they include every purchase search cached this week
        queryClient.invalidateQueries(["purchases", "collection"]);

        const { paymentLineItem } = useCreatePurchaseStore.getState();
        if (paymentLineItem.payment.method === PaymentMethod.CREDIT_CARD) {
            invalidateSellerBalance();
        }

        if (updatedPurchase.status === PurchaseStatus.COMMITTED) {
            setIsSuccess(true);
        }

        routeToPurchases(updatedPurchase);
    };

    const routeToPurchases = async (purchase: Purchase) => {
        if (!purchase) {
            return;
        }

        const uri = getPostCreateBookingAction(seller, purchase);
        const shouldShowIncompleteAutoFulfillmentModal =
            uri.startsWith("/purchases/") && hasIncompleteAutoFulfillment(purchase);

        if (shouldShowIncompleteAutoFulfillmentModal) {
            setIncompleteAutoFulfillmentState({ purchase, uri });
            return;
        }

        handleSuccessfulSubmission(() => {
            history.push(uri);
            resetState();
        });
    };

    const handleIncompleteAutoFulfillmentContinue = () => {
        if (!incompleteAutoFulfillmentState) {
            return;
        }

        const uri = incompleteAutoFulfillmentState.uri;
        setIncompleteAutoFulfillmentState(null);

        handleSuccessfulSubmission(() => {
            history.push(uri);
            resetState();
        });
    };

    const handleAdditionalBillingSubmit = async (
        tokenizePaymentParams: TokenizePaymentParams,
        values: CheckoutFormValues,
    ) => {
        const { paymentLineItem } = useCreatePurchaseStore.getState();
        const paymentStatus = paymentLineItem.status;
        const payload = getPayload(values) as CreatePurchaseState;
        const purchasePayload = getAuthNetPurchasePayload(tokenizePaymentParams, payload, paymentStatus);

        try {
            const createdPurchase = await upsertPurchase(purchasePayload);
            handlePaymentSuccess(createdPurchase);
        } catch (error: any) {
            refreshMerchandiseData();
            paymentInputRef.current?.onPaymentError?.(error.response?.data?.global);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const popoverParentId = useMemo(() => `popover-${merchandise.id}`, [merchandise.id]);

    return (
        <div className="grid h-full grid-cols-12" id={popoverParentId}>
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <div className="space-y-7">
                    <h3>Product Details</h3>

                    <XolaFormik<CheckoutFormValues>
                        enableReinitialize
                        enablePrompt={false}
                        initialValues={initialValues}
                        validationSchema={object().shape(validationSchema)}
                        onSubmit={handleSubmit}
                    >
                        <div className="space-y-4">
                            <CreatePurchaseMerchandiseFields merchandise={merchandise} />

                            <hr className="!my-8 text-gray-light" />
                            <CustomLineItemList />

                            <div className="grid grid-cols-12">
                                <div className="col-span-12">
                                    {showCustomerFieldsPreference && (
                                        <CreatePurchaseCustomerFields product={merchandise} />
                                    )}

                                    {shouldShowPaymentMethods && (
                                        <CreatePurchasePaymentDetails
                                            ref={paymentInputRef}
                                            validate={() => paymentInputRef.current?.getError?.()}
                                            onAdditionalDetailsSubmit={handleAdditionalBillingSubmit}
                                        />
                                    )}
                                </div>
                            </div>

                            <hr className="!mt-4 !mb-8 text-gray-light" />

                            <div>
                                <CreatePurchaseInternalNoteFields />
                            </div>

                            <CheckoutPurchaseScreenFooter
                                isLoading={isLoading}
                                secondaryText="Cancel"
                                isSuccess={isSuccess}
                                onCancel={onBack}
                            />

                            <ScrollToFieldError />
                        </div>
                    </XolaFormik>
                </div>
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>

            {removeCouponModal.isVisible && (
                <RemoveCouponConfirmModal isOpen={removeCouponModal.isVisible} onClose={removeCouponModal.hide} />
            )}

            <IncompleteAutomaticFulfillmentModal
                isOpen={!!incompleteAutoFulfillmentState}
                purchase={incompleteAutoFulfillmentState?.purchase ?? null}
                onClose={handleIncompleteAutoFulfillmentContinue}
                onModifyPurchase={handleIncompleteAutoFulfillmentContinue}
            />
        </div>
    );
};
